import { html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Coupon } from '../chat/types.js';
import styles from '../../styles/mariana-coupon-cards.css?inline';
import { BaseCardCarousel } from './base-card-carousel.js';

/**
 * Coupon Cards Component with Horizontal Scroll
 * Displays a list of coupons in horizontally scrollable cards with scroll indicators
 */
@customElement('mariana-coupon-cards')
export class MarianaCouponCards extends BaseCardCarousel<Coupon> {
  static readonly styles = unsafeCSS(styles);

  private get isActionLocked(): boolean {
    return this.hasAttribute('data-action-pending') || this.hasAttribute('data-action-locked');
  }

  @property({ type: Array })
  get coupons(): Coupon[] {
    return this.cards;
  }

  set coupons(value: Coupon[]) {
    this.cards = value;
  }

  @state()
  private selectedCouponIds: Set<number> = new Set();

  protected getContainerClass(): string {
    return 'coupons-scroll-container';
  }

  protected getCardClass(): string {
    return 'coupon-card';
  }

  private handleSelectCoupon(event: Event, coupon: Coupon, forceChecked?: boolean) {
    if (this.isActionLocked) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.stopPropagation();
    if (!coupon.id) return;

    const couponId = coupon.id;

    const shouldSelect = typeof forceChecked === 'boolean'
      ? forceChecked
      : !this.selectedCouponIds.has(couponId);

    if (shouldSelect) {
      this.selectedCouponIds.add(couponId);
    } else {
      this.selectedCouponIds.delete(couponId);
    }

    // Trigger re-render by creating a new Set
    this.selectedCouponIds = new Set(this.selectedCouponIds);

    // Notify parent of selection changes so the ids can be included in the next payload
    this.dispatchEvent(new CustomEvent('coupon-selection-changed', {
      detail: { selectedIds: [...this.selectedCouponIds].map(String) },
      bubbles: true,
      composed: true,
    }));
  }

  protected renderCard(coupon: Coupon, index: number) {
    const isSelected = coupon.id ? this.selectedCouponIds.has(coupon.id) : false;

    return html`
      <div class="coupon-card ${isSelected ? 'coupon-card--selected' : ''}" data-index="${index}" @click=${(e: Event) => this.handleSelectCoupon(e, coupon)}>
        <div class="coupon-top-strip" style="${coupon.brand_color ? `background-color: ${coupon.brand_color}` : ''}"></div>
        <div class="coupon-header">
          <div class="coupon-logos">
            <img
              src="${coupon.brand_icon || ''}"
              class="coupon-logo-1"
            />
            ${coupon.brand_pay_image ? html`<img src="${new URL('../../assets/images/ContinentePay.svg', import.meta.url).href}" class="coupon-logo-2" />` : ''}
          </div>
          ${coupon.selectable ? html`
            <div class="coupon-header-actions">
              <input
                class="coupon-select-checkbox"
                type="checkbox"
                title="Selecionar cupom"
                aria-label="Selecionar cupom"
                ?checked=${isSelected}
                @change=${(e: Event) => this.handleSelectCoupon(
      e,
      coupon,
      (e.target as HTMLInputElement).checked
    )}
              />
            </div>
          ` : ''}
        </div>
        
        <div class="coupon-image-section">                
          <div class="coupon-discount-badge">
            <img
              src="${coupon.promotion_image || ''}"
              class="discount-badge-img"
            />
          </div>
        </div>
        
        <div class="coupon-info">
          <p class="coupon-title">
            ${coupon.message || 'Sem título'}
          </p>
          
          <p class="coupon-validity">
            ${coupon.date || '-'}
          </p>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mariana-coupon-cards': MarianaCouponCards;
  }
}
